import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const BebakiPramanpatraForm = () => {
  const componentRef = useRef();
  const { language } = useLanguage();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: 'Bebaki_Pramanpatra_Arj.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white">
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm;
            }
            body {
              background-color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>
      
      {/* Controls Container - Hidden when printing */}
      <div className="max-w-4xl mx-auto mb-6 flex justify-end space-x-4 print:hidden px-4">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'प्रिंट करा' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'पीडीएफ डाउनलोड करा' : 'Download PDF'}
        </button>
      </div>

      {/* A4 Paper Container */}
      <div 
        ref={componentRef}
        className="max-w-[210mm] mx-auto bg-white p-12 shadow-lg text-black print:shadow-none print:max-w-none print:w-[210mm] print:h-[297mm] print:mx-0 print:p-12 relative"
        style={{ minHeight: '297mm' }}
      >
        
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold border-b-2 border-black inline-block pb-1">
            {language === 'marathi' ? 'बेबाकी प्रमाणपत्रासाठी करावयाचा अर्ज' : 'Application for No Dues Certificate'}
          </h1>
        </div>

        {/* Date Container */}
        <div className="flex justify-end mb-6 text-[15px]">
          <p>{language === 'marathi' ? 'दिनांक: &nbsp; &nbsp; &nbsp; / &nbsp; &nbsp; &nbsp; /२०' : 'Date: &nbsp; &nbsp; &nbsp; / &nbsp; &nbsp; &nbsp; /20'}</p>
        </div>

        {/* Sender Info Grid */}
        <div className="mb-6 text-[15px] leading-relaxed">
          <p>{language === 'marathi' ? 'प्रति,' : 'To,'}</p>
          <p>{language === 'marathi' ? 'सरपंच / ग्रामपंचायत अधिकारी,' : 'Sarpanch / Gram Panchayat Officer,'}</p>
          
          <div className="mt-4">
            <p className="flex items-end">
              <span className="shrink-0 mr-1">{language === 'marathi' ? 'ग्रामपंचायत कार्यालय' : 'Gram Panchayat Office'}</span>
              <span className="border-b border-dashed border-black w-48"></span>
            </p>
            <p className="flex items-end mt-1">
              <span className="shrink-0 mr-1">{language === 'marathi' ? 'ता.' : 'Taluka:'}</span>
              <span className="border-b border-dashed border-black flex-grow"></span>
              <span className="shrink-0 mx-1">{language === 'marathi' ? 'जि.' : 'Dist.:'}</span>
              <span className="border-b border-dashed border-black flex-grow"></span>
            </p>
          </div>
        </div>

        {/* Subject */}
        <div className="pl-16 mb-6 text-[15px] leading-relaxed">
           <p className="font-bold">{language === 'marathi' ? 'विषय :- बेबाकी प्रमाणपत्र मिळण्याबाबत.' : 'Subject :- Regarding obtaining No Dues Certificate.'}</p>
           <div className="mt-2 text-[15px]">
             <p className="flex items-end">
               <span className="shrink-0 mr-2">{language === 'marathi' ? 'अर्जदार :- श्री./सौ.' : 'Applicant :- Shri./Smt.'}</span>
               <span className="border-b border-dashed border-black flex-grow"></span>
             </p>
             <p className="flex items-end mt-2">
               <span className="shrink-0 ml-12 mr-2">{language === 'marathi' ? 'रा.' : 'Residing at'}</span>
               <span className="border-b border-dashed border-black flex-grow"></span>
               <span className="shrink-0 mx-2">{language === 'marathi' ? 'मो. नं.' : 'Mob. No.'}</span>
               <span className="border-b border-dashed border-black flex-grow"></span>
             </p>
           </div>
        </div>

        {/* Body Text */}
        <div className="mb-4 text-[15px] leading-relaxed">
          <p>{language === 'marathi' ? 'मा. महोदय,' : 'Respected Sir/Madam,'}</p>
          <p className="pl-12 mt-2">
            {language === 'marathi' ? 'वरील विषयान्वये सविनय अर्ज सादर करतो की,' : 'With reference to the above subject, I humbly submit the application that,'}
          </p>
          <p className="mt-2 text-[15px] flex flex-wrap leading-loose items-end">
             <span className="mr-1 mt-1">{language === 'marathi' ? 'माझ्या व माझ्या एकत्र कुटुंबातील सदस्यांच्या नावे ग्रामपंचायत' : 'In the name of myself and my joint family members, in Gram Panchayat'}</span>
             <span className="border-b border-dashed border-black inline-block mt-1 w-48 grow" style={{ marginBottom: '5px'}}></span>
             <span className="mx-1 mt-1">{language === 'marathi' ? 'येथे पुढीलप्रमाणे मालमत्ता' : 'the following properties are there'}</span>
             <span className="w-full">{language === 'marathi' ? 'आहेत.' : '.'}</span>
          </p>
        </div>

        {/* Table */}
        <div className="mb-6 border-r border-b border-black w-full">
          <div className="flex border-t border-l border-black bg-white font-bold h-10">
            <div className="w-[10%] flex items-center px-2 py-1">{language === 'marathi' ? 'अ.क्र.' : 'Sr.No'}</div>
            <div className="w-[25%] flex items-center px-2 border-l border-black py-1">{language === 'marathi' ? 'मिळकत क्र.' : 'Property No.'}</div>
            <div className="w-[65%] flex items-center px-2 border-l border-black py-1">{language === 'marathi' ? 'मालकाचे नाव' : 'Owner\'s Name'}</div>
          </div>
          <div className="flex border-t border-l border-black h-10">
            <div className="w-[10%] px-2 py-1">1.</div>
            <div className="w-[25%] border-l border-black"></div>
            <div className="w-[65%] border-l border-black"></div>
          </div>
          <div className="flex border-t border-l border-black h-10">
            <div className="w-[10%] px-2 py-1">2.</div>
            <div className="w-[25%] border-l border-black"></div>
            <div className="w-[65%] border-l border-black"></div>
          </div>
          <div className="flex border-t border-l border-black h-10">
            <div className="w-[10%] px-2 py-1">3.</div>
            <div className="w-[25%] border-l border-black"></div>
            <div className="w-[65%] border-l border-black"></div>
          </div>
          <div className="flex border-t border-l border-black h-10">
            <div className="w-[10%] px-2 py-1">4.</div>
            <div className="w-[25%] border-l border-black"></div>
            <div className="w-[65%] border-l border-black"></div>
          </div>
          <div className="flex border-t border-l border-black h-10">
            <div className="w-[10%] px-2 py-1">5.</div>
            <div className="w-[25%] border-l border-black"></div>
            <div className="w-[65%] border-l border-black"></div>
          </div>
        </div>

        {/* Paragraph Below Table */}
        <div className="mb-16 text-[15px] leading-relaxed">
          <p className="pl-6">
            {language === 'marathi' 
              ? 'सदरील मिळकती संदर्भातील थकबाकीसह सर्व कर व फीस भरण्यास मी तयार आहे.'
              : 'I am ready to pay all taxes and fees including arrears regarding the said property.'}
          </p>
          <p className="pl-12 mt-3 leading-loose flex flex-wrap items-end">
             <span className="mr-1">{language === 'marathi' ? 'या व्यतिरिक्त माझ्या व माझ्या एकत्र कुटुंबातील सदस्यांच्या नावे ग्रामपंचायत' : 'Apart from this, in the name of myself and my joint family members, in Gram Panchayat'}</span>
             <span className="border-b border-dashed border-black inline-block flex-grow" style={{ marginBottom: '5px' }}></span>
             <span className="ml-1">{language === 'marathi' ? 'येथे' : 'here'}</span>
          </p>
          <p className="mt-1">
             {language === 'marathi' 
               ? 'कोणतीही मालमत्ता नाही.आढळून आल्यास त्यास मी स्वत: जबाबदार राहीन.'
               : 'there is no other property. If found, I will be personally responsible for it.'}
          </p>
          <p className="pl-12 mt-3">
             {language === 'marathi' 
               ? 'करिता मला बेबाकी प्रमाणपत्र देण्यात यावे ही विनंती'
               : 'Therefore, I request that a No Dues Certificate should be given to me.'}
          </p>
        </div>

        {/* Right Footer (Signature) */}
        <div className="flex justify-end text-[15px] mb-8">
          <div className="text-left">
            <p className="mb-2">{language === 'marathi' ? 'स्वाक्षरी / अंगठयाचा ठसा.......................................' : 'Signature / Thumb Impression.......................................'}</p>
            <p>{language === 'marathi' ? 'अर्जदाराचे नाव ..................................................' : 'Applicant\'s Name ..................................................'}</p>
          </div>
        </div>

        {/* Website Link (Absolute at the bottom left) */}
        <div className="absolute bottom-12 left-12 text-blue-700 text-sm font-semibold underline">
          www.majhigrampanchayat.com
        </div>

      </div>
    </div>
  );
};

export default BebakiPramanpatraForm;
